"use client";

import { Calendar, FileText, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheProductionWorkflow() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    number: "01",
    title: "Specification",
    description: "We review your CAD files, material requirements, and MIL-SPEC standards to ensure total compliance before production begins.",
    icon: Calendar
  }, {
    number: "02",
    title: "Programming",
    description: "Our engineers translate your data into precise laser paths, optimizing for material depth, contrast, and long-term durability.",
    icon: FileText
  }, {
    number: "03",
    title: "Execution",
    description: "Using industrial-grade fiber and CO2 systems, we mark your components with exact repeatability and verified technical accuracy.",
    icon: Sparkles
  }, {
    number: "04",
    title: "Verification",
    description: "Every batch undergoes final quality inspection and documentation, ensuring your parts are ready for immediate field deployment.",
    icon: Heart
  }];
  const particles = Array.from({
    length: 24
  }, (_, i) => i);
  return <section data-section-id="591" id="the-production-workflow" className="relative overflow-hidden py-32 md:py-40 bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {particles.map((p, i) => <span data-index={i} key={p} className="absolute block rounded-lg" style={{
        width: `${4 + i % 3 * 2}px`,
        height: `${4 + i % 3 * 2}px`,
        top: `${i * 37 % 100}%`,
        left: `${i * 53 % 100}%`,
        backgroundColor: "hsl(45 68% 47% / 0.1)",
        filter: "blur(1px)",
        animation: `floatParticle ${8 + i % 5}s ease-in-out ${i * 0.3}s infinite alternate`
      }} />)}
      </div>

      <style>{`
        @keyframes floatParticle {
          0% { transform: translate(0, 0); opacity: 0.3; }
          100% { transform: translate(20px, -30px); opacity: 0.8; }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 0 0 hsl(45 68% 47% / 0.4); }
          50% { box-shadow: 0 0 20px 4px hsl(45 68% 47% / 0.5); }
        }
      `}</style>

      <div className="relative mx-auto max-w-7xl px-6">
        {}
        <div ref={headerRef} className={`mx-auto max-w-[760px] text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block text-xs font-default font-medium uppercase tracking-[0.25em] text-primary">
            Operational Flow
          </span>
          <h2 className="mt-6 font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-foreground">
            The <em className="italic font-normal text-primary">precision</em> production cycle.
          </h2>
          <p className="mt-6 font-default text-base md:text-lg leading-relaxed text-muted-foreground">
            From initial file submission to final verification, our process is engineered for compliance, repeatability, and industrial-grade performance.
          </p>
        </div>

        {}
        <div ref={timelineRef} className="relative mt-20 md:mt-24">
          {}
          <div className="pointer-events-none absolute top-1/2 left-0 right-0 hidden lg:block" aria-hidden="true">
            <div className="h-px w-full bg-border" />
          </div>

          {}
          <div className="pointer-events-none absolute top-0 bottom-0 left-8 w-px lg:hidden bg-border" aria-hidden="true" />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-6 relative">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const isStaggered = i % 2 === 1;
            return <div data-index={i} key={step.number} className={`relative transition-all duration-1000 ease-out ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} lg:${isStaggered ? "translate-y-0 lg:-mt-12" : "lg:mt-12"}`} style={{
              transitionDelay: `${i * 150}ms`
            }}>
                  {}
                  <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 rounded-lg bg-primary" style={{
                top: isStaggered ? "calc(100% + 3rem)" : "-3rem",
                width: "12px",
                height: "12px",
                boxShadow: "0 0 16px 2px hsl(45 68% 47% / 0.6)"
              }} aria-hidden="true" />

                  {}
                  <div className="lg:hidden absolute left-8 -translate-x-1/2 top-8 rounded-lg bg-primary" style={{
                width: "12px",
                height: "12px",
                boxShadow: "0 0 16px 2px hsl(45 68% 47% / 0.6)"
              }} aria-hidden="true" />

                  <div className="relative h-full rounded-lg p-8 backdrop-blur-sm ml-16 lg:ml-0 transition-all duration-500 hover:-translate-y-1 bg-card border border-border">
                    <div className="font-default text-5xl font-bold leading-none text-primary">
                      {step.number}
                    </div>

                    <h3 className="mt-6 font-default text-2xl font-semibold text-foreground">
                      {step.title}
                    </h3>

                    <p className="mt-3 font-default text-sm leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>

                    <div className="mt-8 pt-6 border-t border-border">
                      <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 md:mt-24 flex justify-center transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild size="lg" className="font-default tracking-wide px-8 h-12 rounded-lg transition-all duration-300 hover:scale-[1.02] bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg">
            <Link to="/ContactPage">Submit your project</Link>
          </Button>
        </div>
      </div>
    </section>;
}